/**
 * SettingsPage Component
 *
 * User settings page with sub-navigation layout.
 * Features personal data management and email/password sections.
 *
 * @module pages/SettingsPage
 */

import { memo, useCallback, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { Building2, MapPin, Shield, User } from 'lucide-react';

import { SubNavLayout } from '@/components/common/SubNavLayout';
import type { SubNavItem } from '@/components/common/SubNavLayout';
import { useGeoMarkupStore } from '@/store';

import { EmailPasswordSection } from './components/EmailPasswordSection';
import { LocationSection } from './components/LocationSection';
import { PersonalDataSection } from './components/PersonalDataSection';
import { WorkspaceSection } from './components/WorkspaceSection';
import {
  SETTINGS_SECTIONS,
  resolveSettingsSection,
  type SettingsLocationState,
  type SettingsSection,
} from './constants';
import styles from './SettingsPage.module.css';

export const SettingsPage = memo(function SettingsPage(): ReactElement {
  const { t } = useTranslation();
  const { state } = useLocation();
  // Seed the active section from navigation state so a full remount of this
  // page (e.g. returning from the out-of-AppShell map configurator) restores
  // the section the user came from instead of always resetting to the default.
  // The URL stays clean — settings sections are not meant to be deep-linkable.
  const [activeSection, setActiveSection] = useState<SettingsSection>(() =>
    resolveSettingsSection((state as SettingsLocationState | null)?.section)
  );

  const handleSectionChange = useCallback((section: string) => {
    setActiveSection(resolveSettingsSection(section));
  }, []);

  const navItems = useMemo<SubNavItem[]>(
    () => [
      {
        id: SETTINGS_SECTIONS.personal,
        label: t('settings.nav.personalData'),
        icon: User,
      },
      {
        id: SETTINGS_SECTIONS.emailPassword,
        label: t('settings.nav.emailPassword'),
        icon: Shield,
      },
      {
        id: SETTINGS_SECTIONS.location,
        label: t('settings.location.title'),
        icon: MapPin,
      },
      {
        id: SETTINGS_SECTIONS.workspace,
        label: t('settings.nav.workspace'),
        icon: Building2,
      },
    ],
    [t]
  );

  const { displayName, email, avatarUrl } = useGeoMarkupStore(
    useShallow((state) => ({
      displayName: state.auth.user?.displayName ?? '',
      email: state.auth.user?.email ?? '',
      avatarUrl: state.auth.user?.avatarUrl ?? null,
    }))
  );

  const handleNavigateToEmail = useCallback(() => {
    handleSectionChange(SETTINGS_SECTIONS.emailPassword);
  }, [handleSectionChange]);

  return (
    <SubNavLayout
      title={t('settings.title')}
      subtitle={t('settings.subtitle')}
      items={navItems}
      activeItem={activeSection}
      onItemChange={handleSectionChange}
    >
      <div className={styles.pageContent}>
        {activeSection === SETTINGS_SECTIONS.personal && (
          <PersonalDataSection
            displayName={displayName}
            email={email}
            avatarUrl={avatarUrl}
            onNavigateToEmail={handleNavigateToEmail}
          />
        )}
        {activeSection === SETTINGS_SECTIONS.emailPassword && (
          <EmailPasswordSection currentEmail={email} />
        )}
        {activeSection === SETTINGS_SECTIONS.location && <LocationSection />}
        {activeSection === SETTINGS_SECTIONS.workspace && <WorkspaceSection />}
      </div>
    </SubNavLayout>
  );
});

SettingsPage.displayName = 'SettingsPage';
